import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { NuevaActaForm } from '../components/Concursos/Gestion/ListadoActas/AdjuntarNuevaActa';
import { ActaProps } from '../components/Concursos/Gestion/ListadoActas/ListadoActas';
import { ACTASAPI } from '../const';
import { isPendingAction } from '../utils/redux';

type FirmaProps = {
  firmado: boolean;
  idFirmaActa: number;
};

export type ActaWithFirma = ActaProps & Partial<{ firmoUsuario: boolean; idFirmaActa: number }>;
type State = {
  actas: null | ActaWithFirma[];
  actasPublicas: null | ActaPublicaProps[];
  firmas: null | FirmaProps[];
  error: string;
  success: string;
  loading: boolean;
  file: string;
  filePublico: string;
};

const initialState: State = {
  actas: null,
  actasPublicas: null,
  firmas: null,
  error: '',
  loading: false,
  success: '',
  file: '',
  filePublico: '',
};
const sliceName = 'actas';

type GetActaValues = { idActaConcurso: number };
export const getActaFile = createAsyncThunk<string, GetActaValues>(
  `${sliceName}/get/file`,
  async ({ idActaConcurso }) => {
    const { data } = await axios.get(`${ACTASAPI}/actas/file/${idActaConcurso}`, {
      responseType: 'blob',
    });

    const file = new Blob([data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(file);

    return url;
  },
);
type GetActasValues = { idLlamadoConcurso: number };

type ActaPublicaProps = {
  fechaCreacion: string;
  idActaConcurso: number;
  titulo: string;
};

export const getActasPublicas = createAsyncThunk<ActaPublicaProps[], GetActasValues>(
  `${sliceName}/getPublicas`,
  async ({ idLlamadoConcurso }) => {
    const { data } = await axios.get(
      `${ACTASAPI}/public/llamado-concurso/${idLlamadoConcurso}/actas-publicas`,
    );
    return data;
  },
);

type GetActaPublicaValues = { idActaConcurso: number };
export const getActaPublicaFile = createAsyncThunk<string, GetActaPublicaValues>(
  `${sliceName}/get/filePublico`,
  async ({ idActaConcurso }) => {
    const { data } = await axios.get(`${ACTASAPI}/public/actas-publicas/${idActaConcurso}/file`, {
      responseType: 'blob',
    });

    const file = new Blob([data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(file);

    window.open(url);

    return url;
  },
);

export const getActas = createAsyncThunk<ActaProps[], GetActasValues>(
  `${sliceName}/get`,
  async ({ idLlamadoConcurso }) => {
    const { data } = await axios.get(`${ACTASAPI}/actas/llamadoconcurso/${idLlamadoConcurso}`);
    return data;
  },
);

type GetFirmasValues = { idLlamadoConcurso: number };
export const getFirmas = createAsyncThunk<FirmaProps[], GetFirmasValues>(
  `${sliceName}/get/firmas`,
  async ({ idLlamadoConcurso }, { dispatch }) => {
    await dispatch(getActas({ idLlamadoConcurso }));

    const { data } = await axios.get(
      `${ACTASAPI}/firma-acta-concurso/llamadoconcurso/${idLlamadoConcurso}`,
    );
    const { firmasActaConcurso } = data;
    return firmasActaConcurso;
  },
);

type PostValues = { idLlamadoConcurso: number; valores: Record<string, boolean> };
export const postGenerarAprobacion = createAsyncThunk<void, PostValues>(
  `${sliceName}/aprobacion/post`,
  async ({ idLlamadoConcurso, valores }, { dispatch }) => {
    const { vistaPublica, ...etcValores } = valores;
    const finalData = Object.keys(etcValores).reduce<string[]>((acc, it) => {
      const val = etcValores[it];
      const idUsuarioComite = it.substring(0, it.indexOf('_'));
      if (val === true) {
        return [...acc, idUsuarioComite];
      }
      return acc;
    }, []);

    const finalPost = { presente: finalData, vistaPublica };
    await axios({
      url: `${ACTASAPI}/actas/acta1`,
      params: { idLlamadoConcurso },
      data: finalPost,
      method: 'POST',
    });
    dispatch(getActas({ idLlamadoConcurso }));
  },
);

type PostFirmarValues = {
  idFirmaActa: number;
  firmaDisconformidad: boolean;
  firmado?: boolean;
  motivoDisconformidad: string;
};
export const postFimarActa = createAsyncThunk<void, PostFirmarValues>(
  `${sliceName}/firmar/post`,
  async ({ idFirmaActa, ...data }) => {
    await axios({
      url: `${ACTASAPI}/actas/firmar/${idFirmaActa}`,
      data,
      method: 'POST',
    });
  },
);

type PostActaGenerica = NuevaActaForm & { idLlamadoConcurso: number };
export const postActaGenerica = createAsyncThunk<void, PostActaGenerica>(
  `${sliceName}/generica/post`,
  async ({ idLlamadoConcurso, archivoActa, ...valores }, { dispatch }) => {
    const formData = new FormData();
    formData.append('archivoActa', archivoActa);
    formData.append('datosActa', JSON.stringify(valores));

    await axios.post(`${ACTASAPI}/actas/generica/${idLlamadoConcurso}`, formData);
    dispatch(getActas({ idLlamadoConcurso }));
  },
);

const actasSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    cleanMsg: (state) => ({ ...state, success: '', error: '' }),
    cleanState: () => initialState,
    cleanFile: (state) => ({ ...state, file: '' }),
  },
  extraReducers: (builder) => {
    builder.addCase(getFirmas.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(getFirmas.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getFirmas.fulfilled, (state, actions) => {
      state.loading = false;

      const firmas = actions.payload;
      state.actas =
        state.actas?.map((acta, i) => ({
          ...acta,
          firmoUsuario: firmas[i]?.firmado,
          idFirmaActa: firmas[i]?.idFirmaActa,
        })) ?? [];
    });

    builder.addCase(getActas.rejected, (state) => {
      return { ...state, loading: false };
    });
    builder.addCase(getActas.fulfilled, (state, action) => {
      return { ...state, loading: false, actas: action.payload || [] };
    });
    builder.addCase(getActasPublicas.rejected, (state) => {
      return { ...state, loading: false };
    });
    builder.addCase(getActasPublicas.fulfilled, (state, action) => {
      return { ...state, loading: false, actasPublicas: action.payload || [] };
    });
    builder.addCase(getActaFile.rejected, (state) => {
      state.loading = false;
      state.error = 'No se pudo recuperar el acta';
    });
    builder.addCase(getActaFile.fulfilled, (state, action) => {
      state.loading = false;
      state.file = action.payload;
    });

    builder.addCase(postGenerarAprobacion.rejected, (state) => {
      return { ...state, loading: false, error: 'No se pudo generar el acta' };
    });
    builder.addCase(postGenerarAprobacion.fulfilled, (state) => {
      return { ...state, loading: false, success: 'El acta se generó correctamente' };
    });

    builder.addCase(postFimarActa.fulfilled, (state, actions) => {
      state.loading = false;
      state.success = 'Se firmó correctamente';
      state.actas =
        state.actas?.map((e) => {
          if (actions.meta.arg.idFirmaActa === e.idFirmaActa) return { ...e, firmoUsuario: true };
          return e;
        }) ?? [];
    });

    builder.addCase(postFimarActa.rejected, (state) => {
      state.loading = false;
      state.error = 'No se pudo firmar el acta';
    });

    builder.addCase(postActaGenerica.fulfilled, (state) => {
      state.loading = false;
      state.success = 'Acta generada correctamente';
    });

    builder.addCase(postActaGenerica.rejected, (state) => {
      state.loading = false;
      state.error = 'No se pudo generar el acta';
    });
    builder.addCase(getActaPublicaFile.rejected, (state) => {
      state.loading = false;
      state.error = 'No se pudo recuperar el acta';
    });
    builder.addCase(getActaPublicaFile.fulfilled, (state, action) => {
      state.loading = false;
      state.filePublico = action.payload;
    });
    builder.addMatcher(isPendingAction(sliceName), (state) => {
      state.loading = true;
    });
  },
});

export const useActas = () => useSelector((state: any) => state.actas) as State;
export const { cleanMsg, cleanState, cleanFile } = actasSlice.actions;
const actasReducer = actasSlice.reducer;
export default actasReducer;
